import { fetchContentFragmentsFromCms, getLocale } from '@msc-b2c/shared-utils';
import {
  IFetchMainNotesFromFragmentsResponse,
  MainNotesFromFragmentsRequest,
  Note,
  NoteCruiseCriteria,
} from '@msc-b2c/interfaces/interfaces';
import dayjs from 'dayjs';

export async function fetchMainNotesContentFragment(
  serviceType: (string | undefined)[] | undefined
) {
  let response = null;

  const body: MainNotesFromFragmentsRequest = {
    locale: getLocale() ?? '',
    // Higher limit because targeting is applied in FE after fetch
    limit: 100,
  };

  if (serviceType && serviceType.length > 0) {
    body.serviceType = {
      _logOp: 'OR',
      _expressions: serviceType.map((type: string | undefined) => ({
        value: type,
        _operator: 'EQUALS',
      })),
    };
  }

  try {
    const result = await fetchContentFragmentsFromCms<
      IFetchMainNotesFromFragmentsResponse,
      MainNotesFromFragmentsRequest
    >('getMyAreaNotes', body);

    response = result?.data?.noteData?.items;
  } catch (error) {
    console.error('Error fetching main notes:', error);
  }
  return response;
}

const isConfiguredList = (values: string[] | null | undefined): values is string[] =>
  Array.isArray(values) && values.length > 0;

const isConfiguredString = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim() !== '';

const hasDepartureDateTargeting = (note: Note): boolean =>
  Boolean(note.departureDateFrom) || Boolean(note.departureDateTo);

const hasOtherTargetingFields = (note: Note): boolean =>
  isConfiguredList(note.cruiseArea) ||
  isConfiguredList(note.departurePort) ||
  isConfiguredList(note.shipCode);

/**
 * Parses comma-separated cruise IDs from the CF text field.
 */
export const parseCruiseIds = (cruiseId: string): string[] =>
  cruiseId
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

/**
 * Validates CF targeting configuration:
 * - all fields empty -> invalid
 * - cruiseId populated -> valid (other fields ignored at match time)
 * - no cruiseId -> departure date is required; other fields alone are invalid
 */
export const isValidNoteTargetingConfiguration = (note: Note): boolean => {
  if (isConfiguredString(note.cruiseId)) {
    return true;
  }

  if (!hasDepartureDateTargeting(note)) {
    return false;
  }

  return hasDepartureDateTargeting(note) || hasOtherTargetingFields(note);
};

const matchesCruiseId = (
  configuredCruiseIds: string | null | undefined,
  cruiseId: string | undefined
): boolean => {
  if (!isConfiguredString(configuredCruiseIds)) {
    return false;
  }
  if (!cruiseId) {
    return false;
  }

  return parseCruiseIds(configuredCruiseIds).includes(cruiseId);
};

const matchesConfiguredList = (
  configuredValues: string[] | null | undefined,
  cruiseValue: string | undefined
): boolean => {
  if (!isConfiguredList(configuredValues)) {
    return true;
  }
  if (!cruiseValue) {
    return false;
  }
  return configuredValues.includes(cruiseValue);
};

const matchesCruiseArea = (
  configuredAreas: string[] | null | undefined,
  cruiseArea: string | undefined
): boolean => {
  if (!isConfiguredList(configuredAreas)) {
    return true;
  }
  if (!cruiseArea) {
    return false;
  }

  const areaParts = cruiseArea
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);

  return areaParts.some((part) => configuredAreas.includes(part));
};

const matchesDepartureDates = (note: Note, departureDate: string | undefined): boolean => {
  if (!hasDepartureDateTargeting(note)) {
    return true;
  }
  if (!departureDate) {
    return false;
  }

  const current = dayjs(departureDate);
  if (!current.isValid()) {
    return false;
  }

  if (note.departureDateFrom) {
    const from = dayjs(note.departureDateFrom);
    if (from.isValid() && current.isBefore(from, 'day')) {
      return false;
    }
  }

  if (note.departureDateTo) {
    const to = dayjs(note.departureDateTo);
    if (to.isValid() && current.isAfter(to, 'day')) {
      return false;
    }
  }

  return true;
};

const matchesStandardTargeting = (note: Note, criteria: NoteCruiseCriteria): boolean =>
  matchesCruiseArea(note.cruiseArea, criteria.cruiseArea) &&
  matchesConfiguredList(note.departurePort, criteria.departurePort) &&
  matchesConfiguredList(note.shipCode, criteria.shipCode) &&
  matchesDepartureDates(note, criteria.departureDate);

/**
 * Filters notes by cruise targeting rules from the CF:
 * - cruiseId populated -> match only cruiseId, ignore all other fields
 * - no cruiseId -> departure date required; other empty fields are wildcards
 * - invalid / all-empty targeting -> excluded
 */
export function filterNotesByCruiseCriteria(
  notes: Note[] | null | undefined,
  criteria: NoteCruiseCriteria
): Note[] {
  if (!notes?.length) {
    return [];
  }

  return notes.filter((note) => {
    if (!isValidNoteTargetingConfiguration(note)) {
      return false;
    }

    if (isConfiguredString(note.cruiseId)) {
      return matchesCruiseId(note.cruiseId, criteria.cruiseId);
    }

    return matchesStandardTargeting(note, criteria);
  });
}

/**
 * Sorts an array of notes based on their priority order.
 */
export function getSortedNotes(notes: Note[] | null) {
  return notes ? [...notes].sort((note1, note2) => note1.priorityOrder - note2.priorityOrder) : [];
}
